/**
 * The online game's *connection* state machine — everything that can cover the
 * board without being a chess rule. Driven entirely by method calls
 * (`NetworkGameController` translates the wire into them), so it is unit-tested
 * without a socket. The states are the ones `@bn-games/ui-shell`'s
 * `GameStateOverlay` renders.
 *
 * Chess's one departure from the other games: **an opponent dropping is not an
 * overlay.** Their clock keeps running and ours may be the one ticking, so a
 * modal over the board would cost the player who stayed their own time. That
 * state is reported through the board banner instead (`opponentAway`).
 */

/** Board-covering states. `none` means the board is playable. */
export type OnlineOverlay =
  | 'none'
  | 'reconnecting_self'
  | 'game_disconnected'
  | 'rematch_offered'
  | 'rematch_requested'
  | 'rematch_declined'
  | 'game_over';

export type GameOverResult = 'win' | 'loss' | 'draw';

export interface OverlayView {
  readonly overlay: OnlineOverlay;
  /** Absolute epoch-ms deadline of our own reconnect window, or `null`. */
  readonly gracePeriodEndsAt: number | null;
  readonly gameOverResult: GameOverResult | null;
}

export const NO_OVERLAY: OverlayView = { overlay: 'none', gracePeriodEndsAt: null, gameOverResult: null };

/**
 * How long our own reconnect may take before we call it dead. Nothing can
 * tell us while we are offline, so the client mirrors the room server's
 * `RECONNECT_WINDOW_MS`.
 */
export const RECONNECT_GRACE_MS = 300_000;

/** Countdown redraw cadence — the label shows whole seconds. */
const COUNTDOWN_TICK_MS = 1_000;

/** The states that mean "our link is down: act on nothing". */
export function isPausedOverlay(overlay: OnlineOverlay): boolean {
  return overlay === 'reconnecting_self' || overlay === 'game_disconnected';
}

export class OverlayMachine {
  private state: OverlayView = NO_OVERLAY;
  private ticker: ReturnType<typeof setInterval> | null = null;

  constructor(private readonly onChange: () => void) {}

  view(): OverlayView {
    return this.state;
  }

  // --- connection ---

  /** Our own socket went away. A closed game stays closed. */
  selfDisconnected(now: number): void {
    if (this.state.overlay === 'game_disconnected') return;
    // Every failed retry reports "disconnected" again; the window runs from the first.
    if (this.state.overlay === 'reconnecting_self' && this.state.gracePeriodEndsAt !== null) return;
    this.set('reconnecting_self', { gracePeriodEndsAt: now + RECONNECT_GRACE_MS });
  }

  /** The socket is back, but the room has not confirmed the seat yet — so the
   *  overlay stays up, without a countdown, until `RECONNECT_OK` lands. */
  selfConnected(): void {
    if (this.state.overlay !== 'reconnecting_self') return;
    this.set('reconnecting_self', { gracePeriodEndsAt: null });
  }

  /**
   * The room confirmed the seat: whatever we were waiting on is over. A game
   * that had already ended goes back to its result rather than a bare board.
   */
  reconnected(): void {
    const wasOver = this.state.gameOverResult !== null;
    const restored = wasOver ? 'game_over' : 'none';
    const overlay = this.state.overlay === 'reconnecting_self' ? restored : this.state.overlay;
    this.set(overlay, { gracePeriodEndsAt: null });
  }

  /** Terminal: our reconnect window elapsed, or the room refused the rejoin. */
  disconnected(): void {
    this.set('game_disconnected', { gracePeriodEndsAt: null });
  }

  // --- rematch ---

  /** We asked; we are waiting on the opponent. */
  rematchOffered(): void {
    this.set('rematch_offered', {});
  }

  /** The opponent asked. */
  rematchRequested(): void {
    this.set('rematch_requested', {});
  }

  /** Only an answer to our own request: a stray decline must not cover a live board. */
  rematchDeclined(): void {
    if (this.state.overlay === 'rematch_offered') this.set('rematch_declined', {});
  }

  /**
   * A fresh game began (rematch or an agreed restart). While our link is down
   * the connection state stays, but the old result must not come back with it.
   */
  gameStarted(): void {
    const overlay = isPausedOverlay(this.state.overlay) ? this.state.overlay : 'none';
    this.set(overlay, { gameOverResult: null });
  }

  // --- terminals ---

  /** While reconnecting, the result is only recorded; `reconnected` then shows it. */
  gameOver(result: GameOverResult): void {
    if (this.state.overlay === 'game_disconnected') return;
    const overlay = this.state.overlay === 'reconnecting_self' ? this.state.overlay : 'game_over';
    this.set(overlay, { gameOverResult: result });
  }

  /** Dismiss a state the player may walk away from (never a connection one). */
  dismiss(): void {
    if (isPausedOverlay(this.state.overlay)) return;
    this.set('none', { gameOverResult: null });
  }

  dispose(): void {
    this.stopTicker();
  }

  private set(overlay: OnlineOverlay, patch: Partial<OverlayView>): void {
    const next: OverlayView = { ...this.state, ...patch, overlay };
    if (
      next.overlay === this.state.overlay &&
      next.gracePeriodEndsAt === this.state.gracePeriodEndsAt &&
      next.gameOverResult === this.state.gameOverResult
    ) {
      return;
    }
    this.state = next;
    this.syncTicker();
    this.onChange();
  }

  private syncTicker(): void {
    const wants = this.state.gracePeriodEndsAt !== null && this.state.overlay === 'reconnecting_self';
    if (wants && this.ticker === null) {
      this.ticker = setInterval(() => this.tick(), COUNTDOWN_TICK_MS);
    } else if (!wants) {
      this.stopTicker();
    }
  }

  /** Redraw the countdown, or give up once our own window has run down. */
  private tick(): void {
    const endsAt = this.state.gracePeriodEndsAt;
    if (endsAt === null || Date.now() < endsAt) this.onChange();
    else this.disconnected();
  }

  private stopTicker(): void {
    if (this.ticker === null) return;
    clearInterval(this.ticker);
    this.ticker = null;
  }
}
